import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ISidebarMenuItem } from 'app/menu/components/Sidebar/Menu'
import { useTranslation } from 'react-i18next'
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import IconExpandLess from '@mui/icons-material/ExpandLess'
import IconExpandMore from '@mui/icons-material/ExpandMore'
import clsx from 'clsx'
import SvgIcon from '@mui/material/SvgIcon'
import { Box } from '@mui/material'
import EnvironmentFeature from 'core/utils/FeatureFlags/EnvironmentFeature'
import CountryFeature from 'core/utils/FeatureFlags/CountryFeature'
import { RequiredPermission } from 'utils/Subscription/RequiredPermissions'
import Grow from '@mui/material/Grow'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { CheckResult } from '@cbgms/components/environment/environmentUtils'
import { useIsRouteOrChildActiveFunc } from './use-is-route-or-child-active'
import { useHasFeature } from '@cbgms/base/feature-flags'
import { Timing } from '@cbgms/base/utils/timing'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      display: 'flex',
      padding: theme.spacing(1, 2),
      width: '100%',
      justifyContent: 'start',
      textTransform: 'unset',
      borderRadius: 0,
      '&.active': {
        background: theme.palette.action.focus,
        borderLeft: `5px solid ${theme.palette.primary.main}`,
        [`& .${listItemIconClasses.root}`]: {
          color: theme.palette.background.default
        }
      }
    },
    smallPadding: {
      padding: '8px',
      minWidth: 'unset',
      [`& .${listItemIconClasses.root}`]: {
        minWidth: 'unset'
      }
    },
    expand: {
      marginLeft: 'auto',
      order: 2
    },
    icon: {
      color: 'rgba(0, 0, 0, 0.54) !important',
      minWidth: '34px',
      margin: '4px auto'
    },
    menuRoot: {
      zIndex: 1202, // needs zIndex to be sure to be placed on TOP of the scrollbar of the sidemenu (when there is little vertical space)
      position: 'absolute'
    },
    primary: {
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    nestedPadding: {
      paddingLeft: '30px'
    },
    popoverHeader: {
      paddingLeft: '15px',
      '& span': {
        fontWeight: 'bold'
      }
    },
    pointer: {
      cursor: 'pointer'
    }
  })
)

interface SidebarMenuPopOverProps extends ISidebarMenuItem {
  drawerOpen: boolean
  items: ISidebarMenuItem[]
  ID: string
  popoverID: string | null
  anchorEl: HTMLElement | null
  handleClick: (event: React.MouseEvent<EventTarget>, ID: string) => void
  handleClose: (url: string | undefined) => void
  extendedClassName?: string
}

export const SidebarMenuPopOver: React.FC<SidebarMenuPopOverProps> = props => {
  const {
    name,
    translation,
    materialIcon,
    items = [],
    customIcon,
    drawerOpen,
    ID,
    anchorEl,
    popoverID,
    handleClick,
    handleClose,
    url,
    exact,
    extendedClassName
  } = props
  const classes = useStyles()
  const { t } = useTranslation()
  const location = useLocation()
  const hasFeature = useHasFeature()
  const activeForSelfOrChildren = useIsRouteOrChildActiveFunc(location.pathname, items)
  const toLink = url || (items.length > 0 && items[0].url ? items[0].url : `/${name.toLowerCase()}`)
  const popperPositionRef = React.useRef<HTMLAnchorElement | null>(null)
  const handleOnClick = (e: React.MouseEvent<EventTarget>, ID: string) => {
    handleClick(e, ID)
  }

  return (
    <>
      <Button
        data-testid={`menu-item-${name}`}
        aria-owns={anchorEl ? `menu-${ID}` : undefined}
        aria-haspopup='true'
        onClick={(e: React.MouseEvent<EventTarget>) => handleOnClick(e, ID)}
        onMouseOver={(e: React.MouseEvent<EventTarget>) => (!drawerOpen ? handleClick(e, ID) : undefined)}
        className={drawerOpen ? clsx(extendedClassName, classes.menuItem) : clsx(extendedClassName, classes.menuItem, classes.smallPadding)}
        exact={exact}
        component={NavLink}
        activeClassName='active'
        isActive={activeForSelfOrChildren as any}
        to={toLink}
        variant='text'
        ref={popperPositionRef}
      >
        {!!materialIcon && (
          <ListItemIcon className={classes.icon}>
            <SvgIcon component={materialIcon} />
          </ListItemIcon>
        )}
        {!!customIcon && (
          <Box className={classes.icon} display='flex' width='100%' justifyContent='center'>
            {customIcon}
          </Box>
        )}
        {drawerOpen ? <Typography className={classes.primary}>{t(translation)}</Typography> : null}
        {drawerOpen && !anchorEl && <IconExpandMore className={classes.expand} />}
        {drawerOpen && !!anchorEl && <IconExpandLess className={classes.expand} />}
      </Button>
      <Popper
        data-testid='popover-menu'
        placement='right-start'
        id={`menu-${ID}`}
        anchorEl={popperPositionRef.current || anchorEl}
        open={!!anchorEl && popoverID === ID}
        onMouseLeave={() => handleClose(undefined)}
        className={classes.menuRoot}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }} timeout={Timing.fastAnimation}>
            <Paper sx={{ position: 'relative', zIndex: 1202, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
              <List onMouseLeave={() => (!drawerOpen ? handleClose(undefined) : undefined)}>
                {/* render header */}
                <ListItem onClick={() => handleClose(url)} className={`${classes.popoverHeader} ${classes.pointer}`}>
                  <ListItemText primary={t(translation)} classes={{ primary: classes.primary }} />
                </ListItem>
                {items.map((item, index) => {
                  if (item.feature && !hasFeature(item.feature)) {
                    return null
                  }

                  return (
                    <EnvironmentFeature
                      environments={item.environments || []}
                      result={item.environments ? CheckResult.ShowOnMatch : CheckResult.HideOnMatch}
                      key={index}
                    >
                      <CountryFeature countries={item.shopCountries}>
                        <RequiredPermission permission={item.permission}>
                          <ListItem onClick={() => handleClose(item.url)} className={`${classes.nestedPadding} ${classes.pointer}`}>
                            {!!item.materialIcon && (
                              <ListItemIcon className={classes.icon}>
                                <SvgIcon component={item.materialIcon} />
                              </ListItemIcon>
                            )}
                            {!!item.customIcon && item.customIcon}
                            <ListItemText primary={t(item.translation)} classes={{ primary: classes.primary }} />
                          </ListItem>
                        </RequiredPermission>
                      </CountryFeature>
                    </EnvironmentFeature>
                  )
                })}
              </List>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
